import { IconButton, NumberInput, Text, type DataTableColumn } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { DatasetCameraDetails, DatasetDetails } from "@/api/datasetTrainingOverview";
import { HatDataTable } from "@/components/table/HatDataTable";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { formatNumber } from "@/utils";
import { hatcheryTextModes } from "@/app/theme";
import { CameraRow } from "./datasetCameraRows";
import styles from "./DatasetCameraTable.module.css";

function resolutionLabel(resolution: DatasetCameraDetails["resolutions"][number]): string {
  return `${resolution.width}×${resolution.height} (${formatNumber(resolution.video_count)}개)`;
}
function Resolutions({ values }: { values: DatasetCameraDetails["resolutions"] }) {
  if (values.length === 0) return <>—</>;
  // 응답은 파일 수 내림차순이다. 채널 구조 칩의 해상도 면적순과는 별개의 표시다.
  return (
    <span className={styles.resolutions}>
      {values.slice(0, 2).map((value) => (
        <span key={`${value.width}x${value.height}`}>{resolutionLabel(value)}</span>
      ))}
      {values.length > 2 && (
        <HatTextTooltip text={values.slice(2).map(resolutionLabel).join("\n")}>
          <IconButton variant="ghost" size="sm" text={`+${values.length - 2}개`} />
        </HatTextTooltip>
      )}
    </span>
  );
}
const columns: readonly DataTableColumn<CameraRow>[] = [
  { key: "camera", header: "카메라", width: "minmax(120px, 1fr)" },
  {
    key: "probe",
    header: (
      <HatTextTooltip text="헤더 사실이 확인되었거나 읽기 실패가 기록된 원본 영상 수입니다. 각 열의 측정 완전성과는 다릅니다.">
        <span>확인된 영상</span>
      </HatTextTooltip>
    ),
    width: "110px",
  },
  {
    key: "resolutions",
    header: "해상도",
    width: "200px",
    render: (row) => <Resolutions values={row.resolutions} />,
  },
  { key: "fps", header: "실측 FPS", width: "110px" },
  { key: "codecs", header: "코덱", width: "100px" },
  { key: "frames", header: "전체 프레임", width: "110px", align: "right" },
  { key: "duration", header: "영상 길이", width: "100px", align: "right" },
  { key: "files", header: "파일 수", width: "80px", align: "right" },
  { key: "size", header: "전체 크기", width: "110px", align: "right" },
  { key: "masks", header: "mask 수", width: "80px", align: "right" },
];

export function DatasetCameraTable({
  cameras,
  videoDetails,
  onDesignateVideoFps,
  id,
}: {
  cameras: readonly DatasetCameraDetails[] | null;
  videoDetails?: DatasetDetails;
  onDesignateVideoFps?: (fps: number) => Promise<void>;
  id?: string;
}) {
  const source = useMemo(
    () => new StaticTableSource((cameras ?? []).map((camera) => new CameraRow(camera))),
    [cameras],
  );
  useEffect(() => () => source.dispose(), [source]);
  return (
    <section className={styles.card} aria-label="카메라 상세" id={id} tabIndex={-1}>
      <div className={styles.head}>
        <h3 className={styles.heading}>영상·카메라</h3>
        {videoDetails &&
        !cameras?.some((camera) => camera.fps_values.length > 0) &&
        videoDetails.primary_rate_hz != null ? (
          <span className={styles.note}>
            카탈로그 FPS {formatFps(videoDetails.primary_rate_hz)}
          </span>
        ) : null}
      </div>
      {videoDetails && needsVideoFps(videoDetails) ? (
        onDesignateVideoFps ? (
          <VideoFpsDesignation onDesignate={onDesignateVideoFps} />
        ) : (
          <p className={styles.note}>영상 FPS가 아직 확정되지 않았습니다.</p>
        )
      ) : null}
      {cameras === null ? (
        <EmptyState size="sm">카메라 상세 정보가 아직 집계되지 않았습니다.</EmptyState>
      ) : (
        <HatDataTable
          source={source}
          columns={columns}
          ariaLabel="카메라별 파일 통계"
          emptyMessage="등록된 카메라가 없습니다."
          scrollable
          minWidth="1120px"
        />
      )}
      <details className={styles.metadata}>
        <summary>FPS 출처 및 집계 기준</summary>
        {videoDetails ? (
          <p>
            선언 FPS {formatFps(videoDetails.inspect_result.meta?.framerate)} · 카탈로그 FPS{" "}
            {formatFps(videoDetails.primary_rate_hz)}
          </p>
        ) : null}
        <p>
          표의 FPS는 원본 영상 실측값입니다. 카탈로그 FPS는 영상 파일 FPS의 중앙값이며 선언값과 다를
          수 있습니다.
        </p>
        <p>
          프레임·길이는 원본 영상만, 파일 수·크기는 mask를 포함합니다. 프레임·길이·크기는 모든 대상
          파일의 값이 확인된 경우에만 표시합니다.
        </p>
      </details>
    </section>
  );
}

// 카탈로그 FPS가 없는 카메라 데이터셋에만 지정을 허용한다. 일부 파일의 누락은 서버 backfill이 다룬다.
export function needsVideoFps(details: DatasetDetails): boolean {
  return details.camera_stream_count > 0 && details.primary_rate_hz === null;
}

function formatFps(value: number | null | undefined): string {
  return value != null && Number.isFinite(value) ? `${value.toLocaleString("en-US")} fps` : "—";
}

// fps 지정 행. 값 하나를 받아 비어 있는 video 파일에 채운다. 설정 draft 와 달리 저장 버튼을
// 기다리지 않고 즉시 반영된다. 다른 엔드포인트라서 dirty 에 섞을 수 없다.
function VideoFpsDesignation({
  onDesignate,
}: {
  onDesignate: (fps: number) => Promise<void>;
}): ReactNode {
  const [fps, setFps] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const submittable = fps !== null && fps > 0 && !busy;

  async function submit(): Promise<void> {
    if (!submittable) {
      return;
    }
    setBusy(true);
    try {
      await onDesignate(fps);
      // 성공하면 카탈로그 fps 가 생기므로 이 행은 다음 렌더에서 사라진다. 재조회가 늦게 와도
      // 같은 값을 두 번 보내지 않도록 입력을 비운다.
      setFps(null);
    } catch {
      // 호스트가 실패를 알린다. 입력을 보존해 같은 값으로 다시 지정할 수 있게 한다.
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.fpsDesignation}>
      <Text variant={hatcheryTextModes["caption-normal"]} className={styles.note}>
        FPS가 비어 있는 영상에만 지정값을 적용합니다.
      </Text>
      <div className={styles.fpsDesignationRow}>
        {/* 폭은 래퍼가 정한다. 값 하나만 받는 입력이 카드 폭을 다 차지하지 않게 한다. */}
        <div className={styles.fpsInput}>
          <NumberInput
            value={fps}
            onChange={(value) => setFps(value)}
            onEnterPress={() => void submit()}
            mode="decimal"
            allowNegative={false}
            maxDecimalPlaces={3}
            inputSize="sm"
            fullWidth
            placeholder="예: 30"
            aria-label="지정할 fps"
            disabled={busy}
          />
        </div>
        <IconButton
          text={busy ? "지정 중…" : "fps 지정"}
          size="sm"
          variant="surface"
          disabled={!submittable}
          onClick={() => void submit()}
        />
      </div>
    </div>
  );
}
